import './index.css'
import '@disco/state-management/reducers/artists'

import mixpanel from '@disco/api/mixpanel'

import React, { useCallback, useEffect, useState } from 'react'
import CopyToClipboard from 'react-clipboard.js'
import type { Location } from 'react-router-dom'
import { Link } from 'react-router-dom'

import { loadArtists } from '@disco/state-management/actions/artist-actions'
import {
  clientLibraryListDelete,
  clientLibraryListGetAll,
  clientLibraryUpdate,
} from '@disco/state-management/actions/client-library-list-actions'
import type { State as ArtistsState } from '@disco/state-management/reducers/artists'
import { pushUINotification } from '@disco/utils/notifications'

import type { Artist } from 'types/artist'
import type { ClientLibrary, ClientLibraryId } from 'types/client-library'

import emptyClientLibraries1x from 'assets/images/empty-client-libraries.png'
import emptyClientLibraries2x from 'assets/images/empty-client-libraries@2x.png'

import ArtistGridItem from 'components/common/artist-grid-item'
import DocumentHeadTags from 'components/document-head-tags'
import LibrariesTrackQuotaAlert from 'components/libraries/libraries-track-quota-alert'
import LibrariesTrialBlank from 'components/libraries/libraries-trial-blank'
import LibrariesUpgradeAlert from 'components/libraries/libraries-upgrade-alert'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import QRCodeButton from 'components/ui/qr-code-button'
import DataTable from 'components/ui/data-table'
import EllipsisMenu from 'components/ui/ellipsis-menu'
import EmptyState from 'components/ui/empty-state'
import Flex from 'components/ui/flex'
import Grid from 'components/ui/grid'
import HoverCard from 'components/ui/hover-card'
import Icon from 'components/ui/icon'
import InlineLink from 'components/ui/inline-link'
import Lozenge from 'components/ui/lozenge'
import ModalDialog from 'components/ui/modal-dialog'
import PageContainer from 'components/ui/page-container'
import PageHeader from 'components/ui/page-header'
import Spinner from 'components/ui/spinner'
import Switch from 'components/ui/switch'
import Tooltip from 'components/ui/tooltip'

import { useDispatch } from 'hooks/use-redux'

import { RightsHoldersOpportunities } from './rights-holders-opportunities'

type Props = {
  location: Location
  libraries: {
    loading: boolean
    loaded: boolean
    clientLibraries: Array<ClientLibrary>
  }
  artists: ArtistsState & {
    list: Artist[]
  }
  clientLibrariesTrial: boolean
  liveLibrariesEnabled: boolean
  browseLibrariesEnabled: boolean
  isArtist?: boolean
  isLibrariesEmpty: boolean
  typeData: {
    title: string
    value: string
  }
}

const LibrariesList = (props: Props): React.ReactElement<'div'> => {
  const [openLibrariesTrialBlank, setOpenLibrariesTrialBlank] = useState(true)
  const dispatch = useDispatch()
  const {
    libraries,
    artists,
    clientLibrariesTrial,
    liveLibrariesEnabled,
    browseLibrariesEnabled,
    isLibrariesEmpty,
    typeData,
    isArtist,
  } = props
  useEffect(() => {
    if (!libraries.loaded && !libraries.loading) {
      dispatch(clientLibraryListGetAll({}))
    }

    if (!artists.loaded && !artists.loading) {
      dispatch(loadArtists('', 'is_in_client_libraries'))
    }
  }, [])
  useEffect(() => {
    if (artists?.invalidate) {
      dispatch(loadArtists('', 'is_in_client_libraries'))
    }
  }, [artists?.invalidate])

  const handleQRAction = useCallback((action: 'download' | 'copy') => {
    mixpanel.sendPureEvent('Catalog QR code interaction', {
      Action: action,
    })
  }, [])

  const toggleIsLive = (library: ClientLibrary) => (value: boolean) => {
    dispatch(
      clientLibraryUpdate(library['id'], {
        is_live: value,
      }),
    )
  }

  const [confirmationDialog, setConfirmationDialog] = useState(null)

  const hideDeleteConfirmationDialog = () => {
    setConfirmationDialog(null)
  }

  const showDeleteConfirmationDialog = (libraryId: ClientLibraryId) => () => {
    setConfirmationDialog(
      <ModalDialog
        title="Delete Catalog"
        primaryAction={{
          caption: 'Delete',
          onClick: deleteLibrary(libraryId),
        }}
        status="danger"
        onCancel={hideDeleteConfirmationDialog}
      >
        Are you sure you want to permanently delete this Catalog?
      </ModalDialog>,
    )
  }

  const deleteLibrary = (libraryId: ClientLibraryId) => () => {
    dispatch(clientLibraryListDelete(libraryId))
    hideDeleteConfirmationDialog()
  }

  const loadingNode = (
    <div className="Page">
      <DocumentHeadTags title="Catalogs | DISCO" />
      <Spinner />
    </div>
  )

  if (clientLibrariesTrial && openLibrariesTrialBlank) {
    if (!libraries.loaded) {
      return loadingNode
    } else if (isLibrariesEmpty && !browseLibrariesEnabled) {
      return (
        <div className="Page">
          <DocumentHeadTags title="Catalogs | DISCO" />
          <LibrariesTrialBlank
            onClick={() => {
              setOpenLibrariesTrialBlank(false)
            }}
            clientLibrariesTrial={clientLibrariesTrial}
            isLibrariesEmpty={isLibrariesEmpty}
          />
        </div>
      )
    }
  }

  let emptyState = (
    <EmptyState>
      <img
        srcSet={`${emptyClientLibraries1x}, ${emptyClientLibraries2x} 2x`}
        width="162"
        height="165"
      />
      <p>Nothing here yet!</p>
      <Button
        appearance="primary"
        caption="Create new Catalog"
        link="/catalogs/create"
      />
    </EmptyState>
  )

  if (browseLibrariesEnabled) {
    emptyState = (
      <PageContainer.Section title="My Catalogs">
        {emptyState}
      </PageContainer.Section>
    )
  }

  return (
    <div className="Page">
      <DocumentHeadTags title="Catalogs | DISCO" />
      <div className="Page_Content LibrariesList">
        {!clientLibrariesTrial && (!browseLibrariesEnabled || isArtist) && (
          <div className="LibrariesList_TrialAlertContainer">
            <RightsHoldersOpportunities />
          </div>
        )}
        <PageHeader
          title="Catalogs"
          actions={
            <ButtonContainer>
              {!browseLibrariesEnabled && (
                <Button
                  appearance="help"
                  caption="Help & Getting Started"
                  link="https://school.disco.ac/lesson/introduction-to-disco-catalogs"
                  linkTarget="_blank"
                />
              )}

              <Button
                appearance="primary"
                caption="Create new Catalog"
                link="/catalogs/create"
              />
            </ButtonContainer>
          }
        />

        {!liveLibrariesEnabled && !isLibrariesEmpty && (
          <div className="LibrariesList_TrialAlertContainer">
            <LibrariesUpgradeAlert
              clientLibrariesTrial={clientLibrariesTrial}
            />
          </div>
        )}
        {libraries.loaded && isLibrariesEmpty ? (
          <PageContainer>{emptyState}</PageContainer>
        ) : (
          <PageContainer>
            <PageContainer.Section title="My Catalogs">
              <DataTable
                appearance="plain"
                columns={[
                  {
                    name: 'Name',
                    width: 'large',
                    render: (library: ClientLibrary) => (
                      <Flex>
                        <Link
                          to={`/catalogs/${library['id']}`}
                          className="overflow-ellipsis"
                        >
                          <Icon
                            type={library['is_public'] ? 'lock-open' : 'lock'}
                          />{' '}
                          <span translate="no">{library['name']}</span>
                        </Link>
                        {library['primary'] && (
                          <HoverCard
                            position="top"
                            pointer="none"
                            contentClassName="LibrariesList_PrimaryTooltipContent"
                            activator={<Lozenge text="Primary" color="blue" />}
                            content={
                              <>
                                This Catalog is set as your primary Catalog.{' '}
                                <InlineLink
                                  to="https://support.disco.ac/home/catalogs-guide#primary-catalogs"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Find out more.
                                </InlineLink>
                              </>
                            }
                          />
                        )}
                      </Flex>
                    ),
                  },
                  {
                    id: 'playlists_count',
                    name: 'Playlists',
                    width: 'small',
                  },
                  {
                    id: 'tracks_count',
                    name: 'Tracks',
                    width: 'small',
                  },
                  {
                    contentType: 'button',
                    name: 'Status',
                    width: 'content',
                    render: (library: ClientLibrary) => (
                      <Switch
                        disabled={!liveLibrariesEnabled}
                        value={liveLibrariesEnabled && library.is_live}
                        onChange={toggleIsLive(library)}
                      />
                    ),
                  },
                  {
                    contentType: 'button',
                    name: 'Links',
                    width: 'content',
                    render: (library: ClientLibrary) =>
                      !library?.public_url ? null : (
                        <CopyToClipboard
                          component="div"
                          data-clipboard-text={library.public_url}
                          onSuccess={() =>
                            pushUINotification({
                              message: 'Copied!',
                              level: 'success',
                            })
                          }
                        >
                          <Button
                            appearance="icon"
                            icon="link"
                            tooltip="Copy URL"
                          />
                        </CopyToClipboard>
                      ),
                  },
                  {
                    contentType: 'button',
                    width: 'content',
                    render: (library: ClientLibrary) =>
                      !library?.public_url ? null : (
                        <QRCodeButton
                          url={library.public_url}
                          filename={`${library.name || 'catalog'}-qr`}
                          onAction={handleQRAction}
                        />
                      ),
                  },
                  {
                    contentType: 'button',
                    // TODO: Unify Action/Actions column naming across different pages
                    name: 'Action',
                    width: 'content',
                    render: (library: ClientLibrary) => (
                      <EllipsisMenu
                        compact
                        icon="ellipsis"
                        options={[
                          {
                            label: 'Manage',
                            link: {
                              url: `/catalogs/${library['id']}`,
                            },
                            type: 'router-link',
                          },
                          {
                            label: 'Rename',
                            link: {
                              url: `/catalogs/${library['id']}/rename`,
                            },
                            type: 'router-link',
                          },
                          ...(!library['primary'] && library.is_live
                            ? [
                                {
                                  label: 'Set as primary Catalog',
                                  onClick: () =>
                                    dispatch(
                                      clientLibraryUpdate(library.id, {
                                        primary: true,
                                      }),
                                    ),
                                },
                              ]
                            : []),
                          {
                            label: 'Delete',
                            destructive: true,
                            onClick: showDeleteConfirmationDialog(
                              library['id'],
                            ),
                          },
                        ]}
                        hasArrow={false}
                      />
                    ),
                  },
                ]}
                data={libraries.clientLibraries.sort((a, b) => {
                  return a.primary === b.primary ? 0 : a.primary ? -1 : 1
                })}
                rows={6}
                loading={libraries.loading}
              />
            </PageContainer.Section>
          </PageContainer>
        )}

        {libraries.loaded && artists.list.length === 0 ? null : (
          <PageContainer>
            <PageContainer.Section
              title={`${typeData.title}s`}
              description={`Here you can see and manage ${typeData.value}s from your Catalogs. Note that you can also manage them within each Catalog too.`}
              action={
                <Button
                  appearance="primary"
                  caption="View all"
                  link="/catalogs/artists"
                />
              }
            >
              <Grid singleRow size="small">
                {artists.list.map((artist: Artist) => (
                  <ArtistGridItem
                    key={artist['id']}
                    image={artist['image_url']}
                    imageParams={{
                      crop: 'faces',
                    }}
                    title={artist['name']}
                    overlayActions={
                      <Tooltip content={`Edit ${typeData.value}`}>
                        <Button
                          appearance="icon"
                          compact
                          icon="edit"
                          link={{
                            pathname: `/catalogs/artists/${artist['id']}`,
                            state: {
                              modal: true,
                              returnTo: props.location.pathname,
                            },
                          }}
                        />
                      </Tooltip>
                    }
                  />
                ))}
              </Grid>
            </PageContainer.Section>
          </PageContainer>
        )}
      </div>
      {confirmationDialog}
    </div>
  )
}

export default LibrariesList
